/* eslint-disable @typescript-eslint/naming-convention */
import React, {useContext, useRef} from 'react';
import {renderDuration, renderMoment} from 'lib/timeUtils';
import {resource} from '@albert-io/json-api-framework/request/builder';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import makeConstants from 'lib/makeConstants';
import {isEqual} from 'lodash';
import {pushQueryParams} from 'client/history';
import {
  DateRange,
  getLastSchoolYear,
  getThisSchoolYear,
  RANGES
} from '@albert-io/atomic/organisms/DatePicker/DatePicker.helpers';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

import type {ClassroomModelV1, AssignmentModelV3} from '@albert-io/models';

import {IconButton, Popover, Text, WithToggle, WithTooltip} from '@albert-io/atomic';

import moment from 'moment';

import {ReportsContext} from 'client/Reports/Reports.context';

import {
  allDimensions,
  AnalyticsApiResponse,
  Dimension,
  DimensionColumn,
  GuessMetricResponse,
  MetricColumn,
  MetricField,
  QueryVariables,
  QuestionMetricResponse,
  TopLevelFilter
} from './reports.types';
import {AnswerBreakdown} from './Report/Table/Columns/AnswerBreakdown';
import {Performance as PerformanceColumn} from './Report/Table/Columns/Performance';
import StandardsActions from './Report/Table/Columns/StandardsActions';
import AssignmentSubmitted, {
  getStudentAssignmentsView
} from './Report/Table/Columns/AssignmentSubmitted';
import AssignmentActions from './Report/Table/Columns/AssignmentActions';
import QuestionActions from './Report/Table/Columns/QuestionActions';

const DIMENSIONS = makeConstants(...allDimensions);

const assignmentSubmittedColumn: DimensionColumn = {
  columnType: 'dimension',
  dimension: 'assignments',
  key: 'assignment_submitted',
  isAvailable: (dimensions, filters) => {
    return (
      ((!!filters.assignmentFilter || isEqual(dimensions, ['assignments'])) &&
        (!!filters.studentFilter || isEqual(dimensions, ['students']))) ||
      isEqual(dimensions, ['students', 'assignments'])
    );
  },
  sortKey: (dimensions) => {
    if (dimensions.length !== 1) {
      return null;
    }
    const [dimension] = dimensions;

    return `${dimension}.submitted` as `${string}.${string}`;
  },
  title: 'Status',
  renderColumn: (dimensionData, dimensions) => {
    return <AssignmentSubmitted data={dimensionData} dimensions={dimensions} />;
  },
  exportHeaders: () => ['Status'],
  export: (dimensions) => {
    const {submitted} = getStudentAssignmentsView(dimensions);
    return submitted
      ? [`Submitted ${moment(submitted).format('L')} @ ${moment(submitted).format('LT')}`]
      : ['Not Submitted'];
  },
  afterMetrics: true
};

type ReportType = Dimension | 'gradebook';

const DIMENSION_COLUMNS: {[reportType in ReportType]: DimensionColumn[]} = {
  assignments: [
    {
      columnType: 'dimension',
      dimension: 'assignments',
      primaryColumn: true,
      key: 'assignments.name',
      title: 'Name',
      sortKey: 'assignments.name',
      renderColumn: (dimensions) => {
        const assignment = dimensions.find((d) => d.type === 'assignments')?.value;
        return <div>{assignment?.name}</div>;
      },
      exportHeaders: () => ['Name'],
      export: (dimensions) => {
        const assignment = dimensions.find((d) => d.type === 'assignments')?.value;
        return [assignment?.name];
      }
    },
    {
      columnType: 'dimension',
      dimension: 'assignments',
      key: 'assignments.classrooms',
      title: 'Classroom',
      sortKey: null,
      renderColumn: (dimensions) => {
        const assignment = dimensions.find((d) => d.type === 'assignments')?.value;
        return (
          <div>
            {assignment?.classroom_assignments?.map?.((c) => c.classroom.name)?.join?.(', ')}
          </div>
        );
      },
      exportHeaders: null,
      export: null,
      isAvailable: (dimensions, _filters, topLevelFilterType) => {
        return isEqual(dimensions, ['assignments']) && topLevelFilterType !== 'classrooms';
      }
    },
    {
      columnType: 'dimension',
      dimension: 'assignments',
      initialSortDirection: 'desc',
      key: 'assignments.due_date',
      title: 'Due Date',
      sortKey: 'assignments.due_date',
      renderColumn: (dimensions) => {
        const assignment = dimensions.find((d) => d.type === 'assignments')?.value;
        const classroomAssignment = getClassroomAssignmentWithLatestDueDate(
          assignment?.classroom_assignments
        );
        const classroomAssignmentSettings = classroomAssignment?.settings;
        return (
          <div>
            {renderMoment(
              classroomAssignmentSettings?.due_date || assignment?.due_date,
              'dateFormal'
            )}
          </div>
        );
      },
      exportHeaders: () => ['Due Date'],
      export: (dimensions) => {
        const assignment = dimensions.find((d) => d.type === 'assignments')?.value;
        const classroomAssignment = getClassroomAssignmentWithLatestDueDate(
          assignment?.classroom_assignments
        );
        const classroomAssignmentSettings = classroomAssignment?.settings;
        return [
          renderMoment(classroomAssignmentSettings?.due_date || assignment?.due_date, 'dateFormal')
        ];
      }
    },
    assignmentSubmittedColumn,
    {
      columnType: 'dimension',
      dimension: 'assignments',
      key: 'assignments.assignment_actions',
      sortKey: null,
      title: 'Actions',
      renderColumn: (dimensions) => {
        const assignment = dimensions.find((d) => d.type === 'assignments')?.value;

        return (
          <div>
            <AssignmentActions assignment={assignment} />
          </div>
        );
      },
      export: null,
      exportHeaders: null,
      afterMetrics: true
    }
  ],
  students: [
    {
      columnType: 'dimension',
      primaryColumn: true,
      dimension: 'students',
      key: 'students.name',
      title: 'Name',
      sortKey: 'students.last_name',
      renderColumn: (dimensions) => {
        const student = dimensions.find((d) => d.type === 'students')?.value;
        return (
          <div>
            {student?.first_name || ''} {student?.last_name || ''}
          </div>
        );
      },
      exportHeaders: () => ['First Name', 'Last Name'],
      export: (dimensions) => {
        const student = dimensions.find((d) => d.type === 'students')?.value;
        return [student?.first_name, student?.last_name];
      }
    },
    {
      columnType: 'dimension',
      dimension: 'students',
      key: 'students.email',
      title: 'Email',
      sortKey: 'students.email',
      renderColumn: (dimensions) => {
        const student = dimensions.find((d) => d.type === 'students')?.value;
        return <span>{student?.email}</span>;
      },
      exportHeaders: () => ['Email'],
      export: (dimensions) => {
        const student = dimensions.find((d) => d.type === 'students')?.value;
        return [student?.email];
      }
    },
    assignmentSubmittedColumn
  ],
  standards: [
    {
      columnType: 'dimension',
      primaryColumn: true,
      dimension: 'standards',
      key: 'standards.title',
      title: 'Standard',
      sortKey: 'standards.title',
      renderColumn: (dimensions) => {
        const standard = dimensions.find((d) => d.type === 'standards')?.value;

        const iconButtonRef = useRef(null);

        return (
          <span className='u-width_100pc u-display_flex u-justify-content_space-between u-align-items_center'>
            <span className='u-mar-r_2'>{standard?.title}</span>

            <WithToggle>
              {({on, onClick}) => (
                <>
                  <WithTooltip content='Show Description' placement='top' enabled={!on}>
                    <IconButton
                      className='u-mar-r_1 reports-table__column-button'
                      label='Description'
                      icon='info-circle'
                      iconStyle='regular'
                      size='s'
                      variant='outlined'
                      color='primary'
                      onClick={onClick}
                      ref={iconButtonRef}
                    />
                  </WithTooltip>
                  {on && (
                    <Popover targetRef={iconButtonRef} position='top'>
                      <MarkdownRendererV2 text={standard?.description} />
                    </Popover>
                  )}
                </>
              )}
            </WithToggle>
          </span>
        );
      },
      exportHeaders: () => ['Standard'],
      export: (dimensions) => {
        const standard = dimensions.find((d) => d.type === 'standards')?.value;
        return [standard?.title];
      }
    },
    {
      columnType: 'dimension',
      dimension: 'standards',
      key: 'standards.standard_set',
      title: 'Standard Set',
      sortKey: 'standard_sets.title',
      renderColumn: (dimensions) => {
        const standard = dimensions.find((d) => d.type === 'standards')?.value;
        return <div>{standard?.standard_set_v1?.title}</div>;
      },
      exportHeaders: () => ['Standard Set'],
      export: (dimensions) => {
        const standard = dimensions.find((d) => d.type === 'standards')?.value;
        return [standard?.standard_set_v1?.title];
      }
    },
    {
      columnType: 'dimension',
      dimension: 'standards',
      key: 'standards.standardsActions',
      sortKey: null,
      title: 'Actions',
      renderColumn: (dimensions) => {
        return <StandardsActions data={dimensions} />;
      },
      export: null,
      exportHeaders: null,
      afterMetrics: true
    }
  ],
  questions: [
    {
      columnType: 'dimension',
      dimension: 'questions',
      primaryColumn: true,
      key: 'questions.title',
      title: 'Name',
      sortKey: 'questions.title',
      renderColumn: (dimensions) => {
        const question = dimensions.find((d) => d.type === 'questions')?.value;
        return <MarkdownRendererV2 text={question?.title} />;
      },
      exportHeaders: () => ['Name'],
      export: (dimensions) => {
        const question = dimensions.find((d) => d.type === 'questions')?.value;
        return [question?.title];
      }
    },
    {
      columnType: 'dimension',
      dimension: 'questions',
      key: 'questions.difficulty',
      title: 'Difficulty',
      sortKey: 'questions.difficulty',
      renderColumn: (dimensions) => {
        const question = dimensions.find((d) => d.type === 'questions')?.value;

        const difficulty = {
          1: 'Easy',
          2: 'Moderate',
          3: 'Difficult'
        }[question?.difficulty];

        return <div>{difficulty}</div>;
      },
      exportHeaders: () => ['Difficulty'],
      export: (dimensions) => {
        const question = dimensions.find((d) => d.type === 'questions')?.value;
        const difficulty = {
          1: 'Easy',
          2: 'Moderate',
          3: 'Difficult'
        }[question?.difficulty];
        return [difficulty];
      }
    },
    {
      columnType: 'dimension',
      dimension: 'questions',
      key: 'questions.question_actions',
      sortKey: null,
      title: 'Actions',
      renderColumn: (dimensions) => {
        return <QuestionActions data={dimensions} />;
      },
      export: null,
      exportHeaders: null,
      afterMetrics: true
    }
  ],
  gradebook: [assignmentSubmittedColumn]
};

export const columnIsAvailable = (
  column: DimensionColumn | MetricColumn,
  dimensions: Dimension[],
  filters: QueryVariables,
  topLevelFilterType: TopLevelFilter
) => {
  return column.isAvailable ? column.isAvailable(dimensions, filters, topLevelFilterType) : true;
};

const getAnswerBreakdownMetrics = (dimensions: Dimension[], filters: QueryVariables) => {
  const metrics: MetricField[] = [
    'guess_metrics.count_correct_attempts',
    'guess_metrics.count_incorrect_attempts',
    'guess_metrics.count_attempts'
  ] as MetricField[];

  // Locations where unattempted shows up:
  // The students dimension if a subject or guide level or assignment filter is present.
  // The questions dimension always.
  // The assignments dimension always.
  if (
    (isEqual(dimensions, ['students']) &&
      (!!filters.subjectFilter || !!filters.guideLevelFilter || !!filters.assignmentFilter)) ||
    isEqual(dimensions, ['questions']) ||
    isEqual(dimensions, ['assignments']) ||
    isEqual(dimensions, ['students', 'assignments'])
  ) {
    metrics.push('question_metrics.count_unattempted');
  }

  return metrics;
};

const getAccuracy = (
  data: AnalyticsApiResponse,
  dimensions: Dimension[],
  filters: QueryVariables
) => {
  const datum = getGuessMetrics(data)?.values;
  if (!datum) return 'N/A';
  let value = '';
  if (
    (isEqual(dimensions, ['students']) && filters.questionFilter) ||
    (isEqual(dimensions, ['questions']) && filters.studentFilter)
  ) {
    value = datum.accuracy === 1 ? 'Correct' : 'Incorrect';
  } else {
    value = `${Math.round((datum?.accuracy || 0) * 100)}%`;
  }

  return value;
};

/*
  note on naming:
  a "metric" and a "metric column" are NOT the same thing.
  A "metric" is the individual field of data we ask the back end for. In some cases, this
  corresponds 1:1 with a metric column, but not always. For example, the "Accuracy" column
  maps directly to the accuracy metric, but the "Answer Breakdown" column
  is a metric column that maps to 3 or 4 metrics depending on context.

  Basically everything displayed in the UI maps to a metric column - including the table columns
  themselves and the items in the metric selector dropdown. Each metric column can choose which
  metrics it wants to load.
*/
const METRIC_COLUMNS: MetricColumn[] = [
  {
    columnType: 'metric',
    initialSortDirection: 'desc',
    metricType: 'renderedTitle',
    key: 'time_spent',
    sortKey: 'guess_metrics.avg_time_spent_per_student',
    renderTitle: ({
      dimensions,
      variables
    }: {
      dimensions: Dimension[];
      variables: QueryVariables;
    }) => {
      return (
        <>
          <b>Time Spent</b>{' '}
          {!dimensions.includes('students') && !variables.studentFilter ? (
            <Text emphasis='low' size='s'>
              (avg per student)
            </Text>
          ) : null}
        </>
      );
    },
    getMetricsToLoad: () => ['guess_metrics.avg_time_spent_per_student'],
    renderColumn: (data) => {
      const datum = getGuessMetrics(data)?.values;
      if (!datum) return <div>N/A</div>;
      return (
        <div>
          {renderDuration(moment.duration(datum?.avg_time_spent_per_student || 0), {
            style: 'hh:mm:ss'
          })}
        </div>
      );
    },
    exportHeaders: (dimensions) =>
      isEqual(dimensions, ['students']) || isEqual(dimensions, ['questions'])
        ? ['Time Spent (avg per student)']
        : ['Time Spent'],
    export: (data) => {
      const datum = getGuessMetrics(data)?.values;
      if (!datum) return ['N/A'];
      return [
        renderDuration(moment.duration(datum?.avg_time_spent_per_student || 0), {
          style: 'hh:mm:ss'
        })
      ];
    }
  },
  {
    columnType: 'metric',
    metricType: 'renderedTitle',
    key: 'performance',
    sortKey: null,
    renderTitle: () => {
      const {performanceCalculateBy} = useContext(ReportsContext);

      if (performanceCalculateBy === 'accuracy') {
        return <b>Accuracy</b>;
      }
      return <b>Grade</b>;
    },
    isAvailable: (dimensions, filters) => {
      return !(
        (isEqual(dimensions, ['students']) && filters.questionFilter) ||
        (isEqual(dimensions, ['questions']) && filters.studentFilter)
      );
    },
    getMetricsToLoad: (_dimensions, _filters, {performanceCalculateBy}) => [
      performanceCalculateBy === 'accuracy' ? 'guess_metrics.accuracy' : 'question_metrics.grade',
      'guess_metrics.count_attempts'
    ],
    renderColumn: (data, dimensions, filters) => {
      return <PerformanceColumn data={data} dimensions={dimensions} filters={filters} />;
    },
    exportHeaders: (_dimensions, _filters, {performanceCalculateBy}) =>
      performanceCalculateBy === 'accuracy' ? ['Accuracy'] : ['Grade'],
    export: (data, dimensions, filters, {performanceCalculateBy}) => {
      if (performanceCalculateBy === 'accuracy') {
        const accuracy = getAccuracy(data, dimensions, filters);

        return [accuracy];
      }

      const grade = getQuestionMetrics(data)?.values.grade;

      return [`${Math.round(grade * 100)}%`];
    }
  },
  {
    columnType: 'metric',
    metricType: 'renderedTitle',
    key: 'answer_breakdown',
    // special case - there is special sort logic for this column handled in HeaderRenderer.tsx
    // in the AnswerBreakdownHeaderCell component
    sortKey: null,
    renderTitle: () => {
      const {variables, dimensions} = useContext(ReportsContext);

      if (
        (isEqual(dimensions, ['students']) && variables.questionFilter) ||
        (isEqual(dimensions, ['questions']) && variables.studentFilter)
      ) {
        return <b>Answer</b>;
      }

      return <div>Answer Breakdown</div>;
    },
    getMetricsToLoad: getAnswerBreakdownMetrics,
    renderColumn: (data) => {
      return <AnswerBreakdown data={data} />;
    },
    exportHeaders: (dimensions, filters) => {
      const metrics = getAnswerBreakdownMetrics(dimensions, filters);

      return metrics.map(
        (m) =>
          ({
            'guess_metrics.count_correct_attempts': 'Correct Attempts',
            'guess_metrics.count_incorrect_attempts': 'Incorrect Attempts',
            'guess_metrics.count_attempts': 'Total Attempts',
            'question_metrics.count_unattempted': 'Unanswered'
          }[m])
      );
    },
    export: (data, dimensions, filters) => {
      const metricsToExport = getAnswerBreakdownMetrics(dimensions, filters);
      const guessMetrics = getGuessMetrics(data)?.values;
      const questionMetrics = getQuestionMetrics(data)?.values;

      const correct = guessMetrics?.count_correct_attempts ?? 'N/A';
      const incorrect = guessMetrics?.count_incorrect_attempts ?? 'N/A';
      const total = guessMetrics?.count_attempts ?? 'N/A';
      const unattempted = questionMetrics?.count_unattempted ?? 'N/A';

      return metricsToExport.map(
        (m) =>
          ({
            'guess_metrics.count_correct_attempts': `${correct}`,
            'guess_metrics.count_incorrect_attempts': `${incorrect}`,
            'guess_metrics.count_attempts': `${total}`,
            'question_metrics.count_unattempted': `${unattempted}`
          }[m])
      );
    }
  }
];

const getClassroomQuery = (classId) =>
  resource('classroom_v1').mandarkEndpoint(['classrooms_v1', classId]).include('school_v5');

function getClassroomsQuery() {
  return resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState(), 'classrooms_v1'])
    .sort('name')
    .pageSize('250');
}

const getTeacherQuery = (teacherId) =>
  resource('teachers_v1').mandarkEndpoint(['teachers_v1', teacherId]).include('school_v5');

const getSchoolQuery = (schoolId: string) =>
  resource('schools_v5').mandarkEndpoint(['schools_v5', schoolId]);

const getDistrictQuery = (districtId: string) =>
  resource('districts_v1').mandarkEndpoint(['districts_v1', districtId]);

function getClassroomDatesQuery(classroomId) {
  return resource('classroom_v1').mandarkEndpoint(['classrooms_v1', classroomId]).fields({
    classroom_v1: 'begin_date,end_date'
  });
}

function getClassroomSubjectsQuery(classroomId) {
  return resource('subjects_v2')
    .mandarkEndpoint(['classrooms_v1', classroomId, 'subjects_v2'])
    .include('guides_v1.guide_levels_v2')
    .filter({
      guides_v1: {
        id: {
          null: false
        }
      },
      included: {
        guides_v1: {}
      }
    })
    .withMeta('guide_v1');
}

const getTeacherSubjectsQuery = (teacherId: string) => {
  return resource('subjects_v2')
    .mandarkEndpoint(['subjects_v2'])
    .filter({
      classrooms_v1: {
        teachers_v1: {
          id: teacherId
        }
      }
    });
};

const getSchoolSubjectsQuery = (schoolId: string) => {
  return resource('subjects_v2')
    .mandarkEndpoint(['subjects_v2'])
    .filter({
      classrooms_v1: {
        school_v5: {
          id: schoolId
        }
      }
    });
};

const getDistrictSubjectsQuery = (districtId: string) => {
  return resource('subjects_v2')
    .mandarkEndpoint(['subjects_v2'])
    .filter({
      classrooms_v1: {
        school_v5: {
          district_v1: {
            id: districtId
          }
        }
      }
    });
};

function isDimension(str: string | Dimension): str is Dimension {
  return allDimensions.includes(str);
}

function isDrillingDown(path: (string | Dimension)[]): boolean {
  return path.filter(isDimension).length >= 2;
}

function isFinalDimension(path: (string | Dimension)[]): boolean {
  return path.filter(isDimension).length === allDimensions.length;
}

function removeMetrics({metricsToRemove, existingMetrics}) {
  const updatedMetrics = existingMetrics.filter((m) => !metricsToRemove.includes(m));
  pushQueryParams({metrics: updatedMetrics.join(',')});
}

function getGuideFromGuideLevelId(subject, guideLevelId) {
  let guides = subject.getGuides();

  while (guides.size) {
    const currentGuide = guides.first();

    if (currentGuide.getId() === guideLevelId) {
      return currentGuide;
    }

    const match = currentGuide
      .getGuideLevels()
      .getCollection()
      .find((gl) => gl.getId() === guideLevelId);

    if (!match) {
      guides = guides.shift();
    } else {
      return currentGuide;
    }
  }
  return null;
}

const getArchivedDateRange = (archivedClassroom: ClassroomModelV1) => {
  const archivedYear = archivedClassroom.getAutoArchiveDate().utc().format('Y');
  // July 1 of year before
  const start = `${archivedYear - 1}-07-01`;
  // June 30 of archived year
  const end = `${archivedYear}-06-30`;

  const lastSchoolYear = getLastSchoolYear();
  const thisSchoolYear = getThisSchoolYear();

  const isLastSchoolYear =
    lastSchoolYear.get('start').isSame(start) && lastSchoolYear.get('end').isSame(end);
  const isThisSchoolYear =
    thisSchoolYear.get('start').isSame(start) && thisSchoolYear.get('end').isSame(end);

  let range: DateRange = RANGES.CUSTOM;
  if (isLastSchoolYear) {
    range = RANGES.LAST_SCHOOL_YEAR;
  } else if (isThisSchoolYear) {
    range = RANGES.THIS_SCHOOL_YEAR;
  }
  return {start, end, range};
};

export const getGuessMetrics = (item: AnalyticsApiResponse): GuessMetricResponse => {
  return item.metrics.filter((metric) => metric.type === 'guess_metrics')[0] as GuessMetricResponse;
};

export const getQuestionMetrics = (item: AnalyticsApiResponse): QuestionMetricResponse => {
  return item.metrics.filter(
    (metric) => metric.type === 'question_metrics'
  )[0] as QuestionMetricResponse;
};

/*
  on the all classes tab, for assignments with multiple classroom assignments
  we want to show the one with the latest due date
  for specific class ids, the backend already filters out the classroom assignments
  so in case the length is only one, just return the first index
 */
const getClassroomAssignmentWithLatestDueDate = (
  assignments: {
    settings: AssignmentModelV3.settings;
  }[] = []
) => {
  const assignment =
    assignments.length === 1
      ? assignments[0]
      : assignments.reduce(
          (latest, current) => {
            const latestDueDate = new Date(latest?.settings?.due_date || 0);
            const currentDueDate = new Date(current?.settings?.due_date || 0);
            return currentDueDate > latestDueDate ? current : latest;
          },
          {
            settings: null
          }
        );
  return assignment;
};

export {
  getGuideFromGuideLevelId,
  allDimensions,
  getClassroomDatesQuery,
  getClassroomQuery,
  getClassroomsQuery,
  getTeacherQuery,
  getSchoolQuery,
  getDistrictQuery,
  getClassroomSubjectsQuery,
  getTeacherSubjectsQuery,
  getSchoolSubjectsQuery,
  getDistrictSubjectsQuery,
  removeMetrics,
  isDimension,
  isDrillingDown,
  isFinalDimension,
  DIMENSIONS,
  getArchivedDateRange,
  METRIC_COLUMNS,
  DIMENSION_COLUMNS
};
